import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReadMore from '@shopDetails/components/ReadMore';

import { MENU_SECTION, PRODUCT_CLICK_LOCATION } from '@conversion/constants';
import { Food } from '@components/ProductCard';

import Tag, { TAG_TYPE } from '@components/Tag';
import { getDiscountPercent, MIN_DISCOUNT } from '@shopDetails/utils/discounts';
import RatingIcon from '@pedidosya/web-fenix/icons/RatingIcon';
import Price from './components/Price';
import DiscountContainer from './components/DiscountContainer';
import TagContainer from './components/TagContainer';
import TagText from './components/TagText';

const getElementId = element => {
  if (!element || element === document) return PRODUCT_CLICK_LOCATION.CARD;
  if (element.id) return element.id;
  return getElementId(element.parentNode);
};

function ProductCard({
  product,
  withRating,
  onClick,
  discount,
  measurementItems,
  index,
  ...props
}) {
  const {
    name,
    image,
    description,
    price,
    priceWithDiscount,
    subsidizedProduct,
    pricePerMeasurementUnit,
    measurementUnit,
    id,
    finalPriceWithoutTaxes,
  } = product;

  const onClickHandler = evt => {
    const clickLocation = getElementId(evt.target);
    onClick(product, clickLocation, MENU_SECTION.MENU);
  };

  const discountPercent = useMemo(() => {
    const percent = getDiscountPercent(product, discount);
    return percent >= MIN_DISCOUNT ? percent : null;
  }, [discount, product]);

  const measurementUnitPerProduct = {
    pricePerMeasurementUnit,
    measurementUnitId: measurementUnit?.id,
    measurementItems,
  };

  return (
    <Food
      index={index}
      productId={id}
      isBoldName={withRating}
      name={name}
      price={
        <Price
          price={price}
          finalPriceWithoutTaxes={finalPriceWithoutTaxes}
          priceWithDiscount={priceWithDiscount}
          subsidizedProduct={subsidizedProduct}
          discount={Boolean(discountPercent)}
        />
      }
      measurementUnit={measurementUnitPerProduct}
      tags={
        <>
          {discountPercent && (
            <DiscountContainer>
              <Tag
                type={TAG_TYPE.DEALS}
                rounded
                text={`${discountPercent} % OFF`}
              />
            </DiscountContainer>
          )}
          {withRating && (
            <TagContainer>
              <Tag
                type={TAG_TYPE.BRAND}
                text={<TagText>más vendido</TagText>}
                icon={<RatingIcon filled ariaHidden="true" size="small" />}
                rounded
              />
            </TagContainer>
          )}
        </>
      }
      image={image}
      onClick={onClickHandler}
      descriptionCollapsible={
        <ReadMore index={index} productId={id} description={description} />
      }
      {...props}
    />
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string.isRequired,
    image: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.number.isRequired,
    priceWithDiscount: PropTypes.number,
    finalPriceWithoutTaxes: PropTypes.number,
    subsidizedProduct: PropTypes.object,
    id: PropTypes.number,
    requiresAgeCheck: PropTypes.bool,
    isFavourite: PropTypes.bool,
    itemId: PropTypes.string,
    pricePerMeasurementUnit: PropTypes.number,
    measurementUnit: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  withRating: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  discount: PropTypes.number,
  measurementItems: PropTypes.array,
  index: PropTypes.number,
};

export default ProductCard;
